import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider, localStorageColorSchemeManager } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { MotionConfig } from 'motion/react';
import type { Me } from '@rm/shared';

import '@fontsource-variable/inter';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/tiptap/styles.css';
import './theme/tokens.css';
import './theme/retro.css';

import { routeTree } from './routeTree.gen';
import { queryClient } from './app/queryClient.js';
import { theme, cssVariablesResolver } from './theme/index.js';
import { applyRetro, isRetro } from './theme/retro.js';
import { apiFetch } from './api/client.js';

const router = createRouter({
  routeTree,
  context: { queryClient, session: null as Me | null },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const colorSchemeManager = localStorageColorSchemeManager({ key: 'rm:color-scheme' });

/**
 * Resolve the session once before the first render.
 *
 * Doing it here rather than inside a route loader means the auth guard is a
 * synchronous check against router context, so there is no flash of the board
 * shell before a signed-out user is bounced to /login.
 */
async function resolveSession(): Promise<Me | null> {
  try {
    // `allowUnauthenticated` matters here: a 401 is the correct answer for a
    // signed-out visitor, not a session that just expired, so it must not set
    // off the redirect-to-login behaviour.
    return await apiFetch<Me>('/api/me', { allowUnauthenticated: true });
  } catch {
    // 401 is the normal signed-out answer; anything else means the server is
    // unwell, and the login page is still the right place to land.
    return null;
  }
}

async function start(): Promise<void> {
  // Before first paint, so a retro viewer never sees a flash of the default look.
  if (isRetro()) applyRetro(true);
  const session = await resolveSession();
  router.update({ context: { queryClient, session } });

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <MantineProvider
        theme={theme}
        cssVariablesResolver={cssVariablesResolver}
        colorSchemeManager={colorSchemeManager}
        defaultColorScheme="auto"
      >
        {/* `reducedMotion="user"` makes every Motion animation honour the OS
            setting without each component having to remember to. */}
        <MotionConfig reducedMotion="user">
          <QueryClientProvider client={queryClient}>
            <ModalsProvider>
              <Notifications position="bottom-right" limit={3} />
              <RouterProvider router={router} />
            </ModalsProvider>
          </QueryClientProvider>
        </MotionConfig>
      </MantineProvider>
    </StrictMode>,
  );
}

void start();
